// Pure logic for the 8-step "order story" strip rendered at the top of the
// portal Order Detail tab. Kept in a sibling .ts module (no JSX) so it can be
// unit-tested directly and to satisfy react-refresh/only-export-components.

import type { OrderRecord, OrderStatus } from '../../types/orders';

export type StoryStepStatus = 'done' | 'current' | 'pending' | 'skipped';

export interface StoryStep {
  key: string;
  label: string;
  status: StoryStepStatus;
  // Raw ISO timestamp for this milestone, or null when the record carries no
  // dedicated timestamp for it. The strip formats this for display.
  timestamp: string | null;
  // Short human label (e.g. "SO 12345", "high confidence") shown under the
  // step when there is no timestamp. Null when nothing extra to show.
  note: string | null;
}

export interface OrderStory {
  steps: StoryStep[];
  cancelled: boolean;
}

// How far along the Sage pipeline each non-cancelled status sits.
// submission_failed stays at the draft stage — the submit attempt bounced, so
// the order has not actually progressed past Draft.
const STATUS_RANK: Record<OrderStatus, number> = {
  draft: 0,
  submission_failed: 0,
  submitted_to_sage: 1,
  confirmed_in_sage: 2,
  accepted: 3,
  shipped: 4,
  order_completed: 5,
  cancelled: 0,
};

interface StepDef {
  key: string;
  label: string;
  reached: (order: OrderRecord, hasInvoice: boolean) => boolean;
  timestamp: (order: OrderRecord) => string | null;
  note: (order: OrderRecord, hasInvoice: boolean) => string | null;
}

const STEP_DEFS: readonly StepDef[] = [
  {
    key: 'email',
    label: 'Email',
    reached: (o) => o.source_email_id != null,
    timestamp: () => null,
    note: (o) => (o.source_type === 'email_llm' ? null : 'No source email'),
  },
  {
    key: 'classified',
    label: 'Classified',
    reached: (o) => o.source_type === 'email_llm',
    timestamp: () => null,
    note: (o) =>
      o.confidence_bucket ? `${o.confidence_bucket} confidence` : null,
  },
  {
    key: 'agent_decision',
    label: 'Agent Decision',
    reached: () => true,
    timestamp: () => null,
    note: (o) => (o.source_type === 'email_llm' ? 'AI agent' : 'Manual entry'),
  },
  {
    key: 'draft',
    label: 'Draft',
    reached: () => true,
    timestamp: (o) => o.created_at,
    note: () => null,
  },
  {
    key: 'submitted',
    label: 'Submitted',
    reached: (o) => STATUS_RANK[o.status] >= 1 || o.sage_submitted_at != null,
    timestamp: (o) => o.sage_submitted_at,
    note: () => null,
  },
  {
    key: 'live_in_sage',
    label: 'Live in Sage',
    reached: (o) =>
      STATUS_RANK[o.status] >= 2 ||
      o.sage_confirmed_at != null ||
      o.sage_order_no != null,
    timestamp: (o) => o.sage_confirmed_at,
    note: (o) => (o.sage_order_no ? `SO ${o.sage_order_no}` : null),
  },
  {
    key: 'acknowledged',
    label: 'Acknowledged',
    reached: (o) => STATUS_RANK[o.status] >= 3,
    timestamp: () => null,
    note: () => null,
  },
  {
    // Physical departure: the warehouse scanned the BOL (status `shipped`
    // via the bol_scan mirror). Completion implies it too — an invoiced
    // order necessarily left the building.
    key: 'shipped',
    label: 'Shipped',
    reached: (o, hasInvoice) =>
      STATUS_RANK[o.status] >= 4 || o.shipped_at != null || hasInvoice,
    timestamp: (o) => o.shipped_at ?? null,
    note: () => null,
  },
  {
    key: 'invoiced',
    label: 'Invoiced',
    reached: (o, hasInvoice) => STATUS_RANK[o.status] >= 5 || hasInvoice,
    timestamp: () => null,
    note: (_o, hasInvoice) => (hasInvoice ? 'Invoiced' : null),
  },
] as const;

/**
 * Project an order (plus whether it has an invoice) into the 8-step story.
 *
 * The furthest *reached* milestone is the frontier: everything at or before it
 * that was reached is `done`; a milestone the pipeline bypassed (e.g. Email /
 * Classified on a manual order) is `skipped`; the first unreached milestone
 * after the frontier is `current` (unless the order is cancelled); the rest are
 * `pending`. Using the frontier rather than raw per-step flags keeps the strip
 * monotonic even when an intermediate Sage timestamp is missing.
 */
export function buildOrderStory(
  order: OrderRecord,
  hasInvoice: boolean,
): OrderStory {
  const cancelled = order.status === 'cancelled';
  const reachedFlags = STEP_DEFS.map((s) => s.reached(order, hasInvoice));

  let lastReached = -1;
  reachedFlags.forEach((r, i) => {
    if (r) lastReached = i;
  });

  const steps: StoryStep[] = STEP_DEFS.map((def, i) => {
    let status: StoryStepStatus;
    if (i < lastReached) {
      status = reachedFlags[i] ? 'done' : 'skipped';
    } else if (i === lastReached) {
      status = 'done';
    } else if (i === lastReached + 1 && !cancelled) {
      status = 'current';
    } else {
      status = 'pending';
    }
    return {
      key: def.key,
      label: def.label,
      status,
      timestamp: def.timestamp(order),
      note: def.note(order, hasInvoice),
    };
  });

  return { steps, cancelled };
}
